import {
  CLASS_BOOST_SD_BIS_NOTIFY_BUTTON,
  CLASS_BOOST_SD_BIS_NOTIFY_BUTTON_HYPERLINK,
  CLASS_BOOST_SD_BIS_NOTIFY_BUTTON_INLINE,
  ID_BACK_IN_STOCK_CONTAINER,
  ID_NOTIFY_BUTTON,
  ID_POPUP,
} from '../constants';
import { bindBackInStockEvents } from '../events/event-handlers';
import { filterVariants } from '../product/variant-utils';
import { applyDynamicStyling } from '../ui/styling';

/**
 * Renders the back-in-stock template and multi-placement notify buttons.
 * Supports floating, inline, and hyperlink button placements.
 * @type{import('../index.d').renderBackInStockTemplate}
 */
export async function renderBackInStockTemplate(context, moduleContext, product) {
  const backInStockTemplate = moduleContext.settings.templateSettings().template;
  const backInStockContainer = document.querySelector(`#${ID_BACK_IN_STOCK_CONTAINER}`);
  const variants = product.variants;
  const filteredVariants = filterVariants(moduleContext, variants);

  if (backInStockContainer) {
    backInStockContainer.innerHTML = await context.templateRender(backInStockTemplate, {
      filteredVariants,
    });

    await renderNotifyMeButton(context, moduleContext, product);
  }
}

/**
 * Enhanced notification button rendering with complete visibility logic
 * @param {object} context - Widget context
 * @param {object} moduleContext - Module context with settings
 * @param {object} product - Product data
 * @returns {Promise<void>} Async operation, no meaningful return value
 */
async function renderNotifyMeButton(context, moduleContext, product) {
  const buttonType = getButtonPlacementType(context);

  let buttonElement = null;

  if (buttonType === 'floating') {
    buttonElement = renderFloatingButton();
  } else {
    const targetContainer = findTargetContainer();

    if (!targetContainer) {
      console.warn(
        `[Back-In-Stock] - Warning: Target selector not found for button type: ${buttonType}`
      );
      return;
    }

    if (buttonType === 'inline') {
      buttonElement = renderInlineButton(targetContainer);
    } else if (buttonType === 'hyperlink') {
      buttonElement = renderHyperlinkButton(targetContainer);
    } else {
      console.warn(`[Back-In-Stock] - Warning: Unsupported button type encountered: ${buttonType}`);
    }
  }

  if (buttonElement) {
    bindBackInStockEvents(context, product);
    applyDynamicStyling(context);
  } else {
    console.error('[Back-In-Stock] - Failed to create notify button element');
  }
}

/**
 * Create button element safely using DOM methods instead of innerHTML
 * @param {'floating'|'inline'|'hyperlink'} buttonType - Button type. Valid values: 'floating', 'inline', or 'hyperlink'.
 * @returns {HTMLElement} Button or anchor element
 */
function createButtonElement(buttonType) {
  let element;

  if (buttonType === 'hyperlink') {
    element = document.createElement('a');
    element.setAttribute('href', '#');
    element.setAttribute('role', 'button');
  } else {
    element = document.createElement('button');
  }

  element.setAttribute('aria-haspopup', 'dialog');
  element.setAttribute('aria-controls', ID_POPUP);
  element.id = ID_NOTIFY_BUTTON;

  // Hide button initially to prevent flash of unwanted content (FOUC)
  // Visibility will be controlled by the render callback in index.js
  element.style.display = 'none';

  switch (buttonType) {
    case 'floating':
      element.className = CLASS_BOOST_SD_BIS_NOTIFY_BUTTON;
      break;
    case 'inline':
      element.className = CLASS_BOOST_SD_BIS_NOTIFY_BUTTON_INLINE;
      break;
    case 'hyperlink':
      element.className = CLASS_BOOST_SD_BIS_NOTIFY_BUTTON_HYPERLINK;
      break;
  }

  return element;
}

/**
 * Get button placement type from display settings
 * @param {object} context - Widget context
 * @returns {string} Placement type: 'floating', 'inline', or 'hyperlink'
 */
function getButtonPlacementType(context) {
  return context.app?.backInStock?.displaySettings?.button?.type || 'floating';
}

/**
 * Get custom selector from simple theme configuration
 * Theme developers can override target selector by defining:
 * window.backInStockConfig = {
 *   targetSelector: '.custom-button-selector'
 * };
 * @returns {string|null} Custom selector string or null if not found
 */
function getCustomSelectorFromTheme() {
  try {
    const config = window.backInStockConfig;
    if (config?.targetSelector && typeof config.targetSelector === 'string') {
      return config.targetSelector;
    }
  } catch (error) {
    console.warn('[Back-In-Stock] - Error reading theme config:', error);
  }
  return null;
}

/**
 * Find target container for inline/hyperlink button placement
 * @returns {Element|null} Target container element or null if not found
 */
function findTargetContainer() {
  const customSelector = getCustomSelectorFromTheme();

  if (customSelector) {
    try {
      const element = document.querySelector(customSelector);
      if (element) {
        return element;
      }
    } catch (error) {
      console.warn(`[Back-In-Stock] - Invalid custom selector: ${customSelector}`, error);
    }
  }

  const defaultSelectors = [
    '.product-form__buttons',
    '.shopify-section--product-form .product-form__buttons',
    '.product-form .product-form__buttons',
    'form[action*="/cart/add"] .product-form__buttons',
    'form[action*="/cart/add"] .product__submit__buttons',
    'form[action*="/cart/add"] .quantity-submit-row__submit',
    'form[action*="/cart/add"] .payment-buttons',
    'form[action*="/cart/add"] .product-form__action-wrapper',
    'form[action*="/cart/add"] .product-form__payment-container',
    'form[action*="/cart/add"] .product-buy-buttons--root',
    '*[data-block-handle="pre-order"]',
    'form[action*="/cart/add"]',
  ];

  for (const selector of defaultSelectors) {
    const element = document.querySelector(selector);
    if (element) {
      return element;
    }
  }

  return null;
}

/**
 * Render floating button
 * @returns {Element|null} Button element or null if creation failed
 */
function renderFloatingButton() {
  try {
    const backInStockContainer = document.querySelector(`#${ID_BACK_IN_STOCK_CONTAINER}`);
    const targetElement = backInStockContainer || document.body;


    const buttonElement = createButtonElement('floating');
    if (!buttonElement) {
      console.error('[Back-In-Stock] - Failed to create floating button element');
      return null;
    }

    targetElement.appendChild(buttonElement);

    return buttonElement;
  } catch (error) {
    console.error('[Back-In-Stock] - Error rendering floating button:', error);
    return null;
  }
}

/**
 * Render inline button
 * @param {Element} targetContainer - Target container element
 * @returns {Element|null} Button element or null if creation failed
 */
function renderInlineButton(targetContainer) {
  try {
    if (!targetContainer) {
      console.error('[Back-In-Stock] - No target container provided for inline button');
      return null;
    }

    const buttonElement = createButtonElement('inline');
    if (!buttonElement) {
      console.error('[Back-In-Stock] - Failed to create inline button element');
      return null;
    }

    targetContainer.appendChild(buttonElement);

    return buttonElement;
  } catch (error) {
    console.error('[Back-In-Stock] - Error rendering inline button:', error);
    return null;
  }
}

/**
 * Render hyperlink button
 * @param {Element} targetContainer - Target container element
 * @returns {Element|null} Button element or null if creation failed
 */
function renderHyperlinkButton(targetContainer) {
  try {
    if (!targetContainer) {
      console.error('[Back-In-Stock] - No target container provided for hyperlink button');
      return null;
    }

    const buttonElement = createButtonElement('hyperlink');

    if (!buttonElement) {
      console.error('[Back-In-Stock] - Failed to create hyperlink button element');
      return null;
    }

    targetContainer.insertBefore(buttonElement, targetContainer.firstChild);

    return buttonElement;
  } catch (error) {
    console.error('[Back-In-Stock] - Error rendering hyperlink button:', error);
    return null;
  }
}
